/**
 * Trail Tracking Utility
 * Tracks user navigation through the sidebar
 */

import api from "@/api";

// Track the last posted trail to prevent duplicates
let lastTrailData = null;
let lastTrailTime = 0;
const TRAIL_DEBOUNCE_MS = 1000; // 1 second debounce window

/**
 * Convert role name to role ID
 * @param {string} roleName - The role name from user info (e.g., "admin", "user")
 * @returns {number} - Role ID (1 for admin, 2 for user)
 */
const getRoleId = (roleName) => {
  if (!roleName) return 2; // default to user
  const normalizedRole = roleName.toLowerCase();
  return normalizedRole === "admin" ? 1 : 2;
};

/**
 * Check if a trail call should be skipped due to deduplication
 * @param {string} position - The position/page name
 * @returns {boolean} - True if should skip (duplicate detected)
 */
const isDuplicate = (position) => {
  const now = Date.now();
  
  // If enough time has passed, it's not a duplicate
  if (now - lastTrailTime > TRAIL_DEBOUNCE_MS) {
    return false;
  }
  
  // If same position within debounce window, it's a duplicate
  return lastTrailData === position;
};

/**
 * Post user trail/audit log for navigation tracking
 * @param {string} position - The sidebar item name (e.g., "help", "logout")
 */
export const postUserTrail = async (position) => {
  try {
    // Check for duplicate calls
    if (isDuplicate(position)) {
      console.log("⏭️ Skipping duplicate trail call for position:", position);
      return;
    }

    // Get user info from localStorage
    const userInfoStr = localStorage.getItem("userInfo");
    const userInfo = userInfoStr ? JSON.parse(userInfoStr) : null;
    
    if (!userInfo) {
      console.warn("User info not found in localStorage");
      return;
    }

    // Prepare the trail data
    const trailData = {
      username: userInfo.username || "",
      roleId: getRoleId(userInfo.roleName),
      position: position,
    };

    console.log("🔍 Posting trail data:", trailData);

    // Update last trail tracking
    lastTrailData = position;
    lastTrailTime = Date.now();

    // Call the API
    const response = await api.postTrail(trailData);
    console.log("✅ Trail posted successfully:", response);
    return response;
  } catch (error) {
    console.error("❌ Error posting trail:", error);
    // Don't throw error, silently fail to avoid disrupting user navigation
  }
};

export default {
  postUserTrail,
};
